import React from "react";
import { Link } from "../components/L";
import { Check, Gift, CreditCard, CalendarClock, ShieldCheck, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import CTABand from "../components/CTABand";
import { useLang } from "../context/LanguageContext";

const Pricing = () => {
  const { lang, t } = useLang();
  const p = t.pricing;
  const badgeIcons = [CreditCard, CalendarClock, ShieldCheck];
  const seo = lang === "de"
    ? { title: "Preise Putzfrau – Fixpreis 43.90 CHF/Std. | MyCleaner.ch", description: "Transparente Preise für Ihre Putzfrau: Fixpreis 43.90 CHF pro Stunde, keine versteckten Kosten, 1. Stunde gratis, keine Kreditkarte, Abrechnung erst am Monatsende.", keywords: "Putzfrau Preis, Putzfrau Kosten Schweiz, Haushaltshilfe Preis, Putzfrau 43.90 CHF" }
    : { title: "Cleaner pricing – flat rate CHF 43.90/hr | MyCleaner.ch", description: "Transparent pricing for your cleaner: flat rate CHF 43.90 per hour, no hidden costs, 1st hour free, no credit card, billed only at month end.", keywords: "cleaner price, cleaning cost Switzerland, home help price, cleaner 43.90 CHF" };

  return (
    <div>
      <Seo pageKey="pricing" title={seo.title} description={seo.description} keywords={seo.keywords} />

      <section className="pt-[116px] pb-16 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-[820px] mx-auto px-5 md:px-6 text-center">
          <p className="text-teal-600 font-semibold text-sm tracking-wide uppercase mb-3">{p.eyebrow}</p>
          <h1 className="font-head font-extrabold text-4xl md:text-5xl leading-tight text-slate-900 mb-5">{p.title}</h1>
          <p className="text-lg text-slate-600">{p.subtitle}</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1000px] mx-auto px-5 md:px-6 grid lg:grid-cols-2 gap-8 items-center">
          <div className="bg-teal-700 rounded-3xl p-10 text-white text-center shadow-xl">
            <div className="inline-flex items-center gap-2 bg-amber-400 text-teal-800 font-semibold text-sm px-4 py-1.5 rounded-full mb-6"><Gift className="w-4 h-4" /> {p.gratis}</div>
            <div className="font-head font-extrabold text-6xl mb-1">43.90</div>
            <div className="text-teal-100 text-lg mb-8">{p.perHour}</div>
            <ul className="text-left space-y-3 mb-8">
              {p.cardList.map((li) => (
                <li key={li} className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-white" /></span>{li}</li>
              ))}
            </ul>
            <Link to="/buchen" className="inline-flex items-center gap-2 bg-teal-700 text-white font-head font-bold text-lg px-8 py-4 rounded-xl hover:bg-teal-800 transition-colors w-full justify-center">{p.book} <ArrowRight className="w-5 h-5" /></Link>
          </div>

          <div>
            <h2 className="font-head font-extrabold text-2xl text-slate-900 mb-6">{p.incl}</h2>
            <ul className="space-y-4">
              {p.included.map((li) => (
                <li key={li} className="flex items-start gap-3"><span className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-white" /></span><span className="text-slate-700 text-[15px]">{li}</span></li>
              ))}
            </ul>
            <div className="mt-8 grid grid-cols-3 gap-3">
              {p.badges.map((bt, idx) => {
                const Icon = badgeIcons[idx]; return (
                <div key={bt} className="bg-slate-50 rounded-xl p-4 text-center"><Icon className="w-6 h-6 text-teal-700 mx-auto mb-2" /><span className="text-xs text-slate-600 font-medium">{bt}</span></div>
              );})}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-amber-50/50">
        <div className="max-w-[900px] mx-auto px-5 md:px-6">
          <h2 className="font-head font-extrabold text-3xl text-slate-900 text-center mb-10">{p.examplesTitle}</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {p.examples.map((ex) => (
              <div key={ex.h} className="bg-white rounded-2xl border border-slate-100 p-6 text-center">
                <div className="text-slate-500 text-sm mb-1">{ex.label}</div>
                <div className="font-head font-bold text-slate-900 text-lg mb-3">{ex.h} {t.booking.hoursUnit}</div>
                <div className="font-head font-extrabold text-3xl text-teal-700">{(ex.h*43.9).toFixed(2)}</div>
                <div className="text-slate-500 text-sm">{p.perEinsatz}</div>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-500 text-sm mt-6">{p.examplesNote}</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[820px] mx-auto px-5 md:px-6">
          <h2 className="font-head font-extrabold text-3xl text-slate-900 text-center mb-10">{p.faqTitle}</h2>
          <div className="space-y-4">
            {t.faqs.slice(0,4).map((f) => (
              <div key={f.q} className="bg-white rounded-xl border border-slate-100 p-5">
                <h3 className="font-head font-semibold text-slate-900 mb-2">{f.q}</h3>
                <p className="text-slate-600 text-[15px] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
};

export default Pricing;
